import * as React from 'react'
import Page from '../../components/page/Page'
import {
  Checkbox,
  createStyles, DialogActions,
  DialogContent, FormControlLabel, FormGroup,
  Grid,
  Paper,
  Theme,
  withStyles,
  WithStyles,
  withTheme,
  WithTheme
} from "@material-ui/core";
import {RouteComponentProps} from "@reach/router";
import MarginRow from "../../components/page/MarginRow";
import TitleBar from "../../components/TitleBar";
import CourseStore from "../../stores/CourseStore";
import {inject, observer} from "mobx-react";
import {observable, when} from "mobx";
import Course from "../../model/Course";
import Progress from "../../components/Progress";
import UserStore from "../../stores/UserStore";
import FormValidator from "../../components/form/FormValidator";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import Tracking from "../../components/Tracking";
import {
  ActivityType,
  CreateCourseInput,
  UpdateCourseInput
} from "../../API";
import ControlTower, {Routes} from "../../components/ControlTower";
import Notify from "../../components/notify/Notify";
import DialogButton from "../../components/form/DialogButton";
import Storage from '@aws-amplify/storage'
import S3UrlCacheStore from "../../stores/S3UrlCacheStore";
import ProgressButton from "../../components/form/ProgressButton";
import {
  createUUID, durationToSeconds,
  getISODateFromDate,
  getISODateToday,
  isoToLocalDate,
  secondsToDuration
} from "../../stores/StoreUtilities";
import CSVReader from "react-csv-reader";
import { saveAs } from 'file-saver'
import Lesson from "../../model/Lesson";
import {format, parse} from "date-fns";
import FormGroupSpacer from "../../components/form/FormGroupSpacer";

const styles = (theme: Theme) => createStyles({
  rootStyle: {
    flexGrow: 1,
    justifyContent: 'top',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh'
  },
  root: {
    flexGrow: 1,
    justifyContent: 'top',
    alignItems: 'center',
  },
  titleBar: {
    flexGrow: 1,
    justifyContent: "space-between",
    width: "100%",
    height: 40,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  item: {
    justifyContent: 'top',
    alignItems: 'center'
  },
  content: {
    marginTop: theme.spacing(2),
    justifyContent: 'flex-start',
  },
  dialogPaper: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    width: '100%',
    marginTop: theme.spacing(1)
  },
  form: {
    width: '100%'
  },
  dialogContent: {
    padding: theme.spacing(1),
    "&:first-child": {
      paddingTop: 4
    }
  },
  dialogActions: {
    justifyContent: "center",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  label: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5)
  },
  formGroup: {
    marginBottom: 0,
    paddingBottom: 0
  },
  progress: {
    width: "100%",
  },
  progressText: {
    fontSize: 14,
    fontWeight: 800,
    padding: "0px 2px",
  },
  progressError: {
    fontSize: 14,
    fontWeight: 800,
    padding: "0px 2px",
    color: "red"
  },
  downloadLink: {
    textDecoration: 'none',
    color: theme.palette.secondary.main,
    marginTop: 4,
  },
  downloadButton: {
    textDecoration: 'none',
    color: theme.palette.secondary.main,
    paddingLeft: 2,
  },
  fileInput: {
    width: 340
  }
})

interface ICourseEditPageProps {
  courseId?: string
  userStore?: UserStore
  courseStore?: CourseStore
  s3UrlCacheStore?: S3UrlCacheStore
  progress?: Progress
  notify?: Notify
}

const correctAnswers = ["", "A", "B", "C", "D"]

@inject("userStore", "courseStore", "progress", "notify", "s3UrlCacheStore")
@observer
class CourseEditPage extends React.Component<WithStyles<typeof styles> & RouteComponentProps & ICourseEditPageProps & WithTheme> {

  @observable isLoading = true
  @observable course?: Course
  @observable values = {
    courseId: "",
    title: "",
    description: "",
    isFree: false,
    coverFile: undefined,
    coverUrl: "",
    creditHours: 0,
    publishDate: format(isoToLocalDate(getISODateToday()), "M/d/yyyy"),
    sunsetDate: format(isoToLocalDate("2099-12-31"), "M/d/yyyy")
  }
  @observable lessons: any = undefined
  @observable isValid: boolean = true
  @observable isProcessing = false
  @observable progressValue: number = 0
  @observable progressText: string = ""

  componentDidMount () {
    const { courseId, userStore, courseStore, progress} = this.props
    this.isLoading = true
    progress!.show("CourseEditPage")
    when(
      () => !userStore!.isLoading,
      async () => {
        if (courseId) {
          const course = await courseStore!.getCourse(courseId!)
          if (course) {
            this.values = {
              courseId: course.id,
              title: course.title,
              description: course.description,
              coverUrl: course.coverUrl,
              coverFile: undefined,
              isFree: course.isFree,
              creditHours: course.creditHours,
              publishDate: course.publishDate ? format(isoToLocalDate(course.publishDate), "M/d/yyyy") : "",
              sunsetDate: course.publishDate ? format(isoToLocalDate(course.sunsetDate), "M/d/yyyy") : "",
            }
            this.course = course
          }
        } else {
          this.values.courseId = createUUID()
        }
        this.isLoading = false
        progress!.hide("CourseEditPage")
      }
    )
  }

  render() {
    const { classes, courseId } = this.props

    const title = courseId ? "Edit Course" : "Add Course"
    const minDate = "1/1/2000"
    
    return (
      <Page title={title}>
        <MarginRow>
          <Grid container className={classes.root} direction="column">
            <TitleBar title={title} className={classes.titleBar}>
            </TitleBar>
            <Paper className={classes.dialogPaper}>
              <FormValidator onSubmit={this.onSubmit} autoComplete="off"
                             name="CourseEditForm" id="CourseEditForm" className={classes.form}>
                <DialogContent className={classes.dialogContent}>
                  <TextFieldValidator
                    autoFocus
                    margin="dense"
                    name="title"
                    label="Title"
                    type="text"
                    variant="standard"
                    validators={{required:true}}
                    onChange={this.onChange}
                    value={this.values.title}
                    fullWidth
                  />
                  <TextFieldValidator
                    margin="dense"
                    name="description"
                    label="Description"
                    type="text"
                    variant="standard"
                    validators={{required:true}}
                    onChange={this.onChange}
                    value={this.values.description}
                    fullWidth
                  />
                  <FormGroup className={classes.formGroup} row>
                    <TextFieldValidator
                      margin="dense"
                      name="publishDate"
                      label="Publish Date"
                      type="text"
                      variant="standard"
                      validators={{required:true, minDate:minDate}}
                      onChange={this.onChange}
                      value={this.values.publishDate}
                      placeholder="m/d/yyyy"
                    />
                    <FormGroupSpacer />
                    <TextFieldValidator
                      margin="dense"
                      name="sunsetDate"
                      label="Sunset Date"
                      type="text"
                      variant="standard"
                      validators={{required:true, minDate:minDate}}
                      onChange={this.onChange}
                      value={this.values.sunsetDate}
                      placeholder="m/d/yyyy"
                    />
                  </FormGroup>
                  <TextFieldValidator
                    margin="dense"
                    name="creditHours"
                    label="Credit Hours"
                    type="text"
                    variant="standard"
                    validators={{required:true, minValue:0, maxValue:52}}
                    onChange={this.onChange}
                    value={this.values.creditHours}
                  />
                  <div className={classes.label}>Cover Image (JPEG)</div>
                  <FormGroup row>
                    <input
                      type='file'
                      accept='image/jpeg'
                      className={classes.fileInput}
                      onChange={(e) => this.onCoverChange(e.target!.files![0])}
                    />
                  </FormGroup>
                  <div className={classes.label}>Lessons (CSV)</div>
                  <div>
                    <CSVReader
                      cssInputClass={classes.fileInput}
                      label=""
                      onFileLoaded={this.onCSVLoaded}
                    />
                    {!courseId &&
                      <div className={classes.downloadLink}>
                        <a href={`${process.env.PUBLIC_URL}/CourseTemplate.csv`} download="CourseTemplate.csv" className={classes.downloadLink}>DOWNLOAD A TEMPLATE</a>
                      </div>
                    }
                    {courseId &&
                      <DialogButton variant="tertiary" onClick={this.downloadCSV} customClassName={classes.downloadButton}>
                        DOWNLOAD CSV
                      </DialogButton>
                    }
                    <div className={classes.progress}>
                      <div className={this.isValid ? classes.progressText : classes.progressError}>
                        {this.progressText}
                      </div>
                    </div>
                  </div>
                  <FormControlLabel
                    value="isFree"
                    control={
                      <Checkbox color="secondary" name="isFree" id="isFree"
                                checked={this.values.isFree}
                                onChange={this.onChange}/>}
                    label="Free class"
                    labelPlacement="end"
                    className={classes.label}
                  />
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <DialogButton variant="secondary" onClick={this.onCancel}>
                    CANCEL
                  </DialogButton>
                  <ProgressButton variant="contained" color="secondary"
                                  type="submit" processing={this.isProcessing}
                                  onClick={this.onSubmit}>
                    SAVE
                  </ProgressButton>
                </DialogActions>
              </FormValidator>
            </Paper>
          </Grid>
        </MarginRow>
      </Page>
    )
  }

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name
    if (name === "isFree") {
      this.values![name] = event.target.checked
    } else {
      this.values[name] = event.target.value
    }
  }

  onCoverChange = (file: any) => {
    console.log("onCoverChange")
    this.values.coverFile = file
  }

  onSubmit = async () => {
    const { courseId, courseStore, userStore, notify } = this.props

    if (!this.isValid) {
      return
    }

    const values = this.values

    if (courseId) {
      Tracking.event({action: 'UpdateCourse'})
      this.isProcessing = true

      if (values.coverFile) {
        const result = await this.uploadCover()
        if (!result) {
          this.isProcessing = false
          return
        }
      }

      const input: UpdateCourseInput = {
        id: courseId,
        title: values.title,
        description: values.description,
        coverUrl: values.coverUrl,
        isFree: values.isFree,
        creditHours: values.creditHours
      }
      if (values.publishDate !== "") {
        input.publishDate = getISODateFromDate(parse(values.publishDate, 'M/d/yyyy', new Date()))
      }
      if (values.sunsetDate !== "") {
        input.sunsetDate = getISODateFromDate(parse(values.sunsetDate, 'M/d/yyyy', new Date()))
      }
      if (this.lessons) {
        input.lessonCount = this.lessons.items.length
        input.lessons = JSON.stringify(this.lessons)
      }

      const update = await courseStore!.updateCourse(input)
        .catch((err: Error) => {
          this.isProcessing = false
          notify!.show("error", "Unable to update course")
        })

      if (update) {
        this.isProcessing = false
        userStore!.createActivity(ActivityType.CourseEdit, courseId)
        notify!.show("success", "Course updated!")
        this.onCancel()
      }
    } else {
      Tracking.event({action: 'CreateCourse'})
      this.isProcessing = true

      if (values.courseId === "") {
        values.courseId = createUUID()
      }

      if (values.coverFile) {
        const result = await this.uploadCover()
        if (!result) {
          this.isProcessing = false
          return
        }
      }

      const input: CreateCourseInput = {
        id: values.courseId,
        title: values.title,
        description: values.description,
        coverUrl: values.coverUrl,
        isFree: values.isFree,
        lessonCount: 0,
        creditHours: values.creditHours
      }
      if (values.publishDate !== "") {
        input.publishDate = getISODateFromDate(parse(values.publishDate, 'M/d/yyyy', new Date()))
      }
      if (values.sunsetDate !== "") {
        input.sunsetDate = getISODateFromDate(parse(values.sunsetDate, 'M/d/yyyy', new Date()))
      }
      if (this.lessons) {
        input.lessonCount = this.lessons.items.length
        input.lessons = JSON.stringify(this.lessons)
      }

      const course = await courseStore!.createCourse(input)
        .catch((err: Error) => {
          this.isProcessing = false
          notify!.show("error", "Unable to create course")
        })

      if (course) {
        this.isProcessing = false
        userStore!.createActivity(ActivityType.CourseCreate, course.id)
        notify!.show("success", "Course created!")
        this.onCancel()
      }
    }

  }

  onCancel = () => {
    ControlTower.route(`${Routes.courses}`)
  }

  onCSVLoaded = async (data: any) => {
    this.isValid = true
    this.progressValue = 0
    this.progressText = ""

    const courseId = this.values.courseId
    const lessons = []

    // Verify data
    if (!data || data.length <= 1 || data[0].length < 11 || data[1].length < 11) {
      this.progressText = "Invalid or empty CSV file"
      this.isValid = false
      return
    }

    const header = data[0]
    if (header[0] !== "Lesson" || header[1] !== "Title" || header[2] !== "Video URL" || header[3] !== "Video Duration" ||
      header[4] !== "Description" || header[5] !== "Question" || header[6] !== "Answer A" || header[7] !== "Answer B" ||
      header[8] !== "Answer C" || header[9] !== "Answer D" || header[10] !== "Correct Answer") {
      this.progressText = "Invalid CSV file header"
      this.isValid = false
      return
    }

    // Validate rows
    const lastRow: [] = data[data.length-1]
    const length = lastRow && lastRow.length > 1 ? data.length : data.length - 1
    let isValid = true

    for (let n = 1; n < length && isValid; n++) {
      const row = data[n]

      if (row.length < 11) {
        this.progressText = `Missing columns on row ${n}`
        isValid = false
        continue
      }

      const number = parseInt(row[0])
      if (isNaN(number)) {
        this.progressText = `Invalid Lesson number on row ${n}`
        isValid = false
        continue
      }

      const title = row[1]
      if (title.length === 0) {
        this.progressText = `Title is missing on row ${n}`
        isValid = false
        continue
      }

      const videoUrl = row[2]
      if (videoUrl.length === 0) {
        this.progressText = `Video URL is missing on row ${n}`
        isValid = false
        continue
      }

      // Try parsing it as time (h:m:s)
      let videoDuration = 0
      if (row[3].indexOf(':') > 0) {
        videoDuration = durationToSeconds(row[3])
      } else {
        // Try parsing it as integer seconds
        videoDuration = parseInt(row[3])
        if (isNaN(videoDuration)) {
          this.progressText = `Invalid Video Duration on row ${n}`
          isValid = false
          continue
        }
      }

      const description = row[4]
      if (description.length === 0) {
        this.progressText = `Description is missing on row ${n}`
        isValid = false
        continue
      }

      const question = row[5]
      if (question.length === 0) {
        this.progressText = `Question is missing on row ${n}`
        isValid = false
        continue
      }

      const answers = new Array<string>(4)

      answers[0] = row[6]
      if (answers[0].length === 0) {
        this.progressText = `Answer A is missing on row ${n}`
        isValid = false
        continue
      }

      answers[1] = row[7]
      if (answers[1].length === 0) {
        this.progressText = `Answer B is missing on row ${n}`
        isValid = false
        continue
      }

      answers[2] = row[8]
      if (answers[2].length === 0) {
        this.progressText = `Answer C is missing on row ${n}`
        isValid = false
        continue
      }

      answers[3] = row[9]
      if (answers[3].length === 0) {
        this.progressText = `Answer D is missing on row ${n}`
        isValid = false
        continue
      }

      // Answers are 1-based
      const correctAnswer = correctAnswers.findIndex((answer: string) => row[10] === answer )
      if (correctAnswer < 1 ) {
        this.progressText = `Correct Answer is invalid on row ${n}`
        isValid = false
        continue
      }

      const standards = row.length >= 12 ? row[11] : ""

      const lesson = {
        id: `${courseId}-${number}`,
        courseId: courseId,
        number: number,
        title: title,
        videoUrl: videoUrl,
        videoDuration: videoDuration,
        description: description,
        standards: standards,
        questions: {
          items: [
            {
              id: `${courseId}-${number}-q1`,
              question: question,
              answers: answers,
              correctAnswer: correctAnswer
            }
          ]
        }
      }

      // this.progressText = `${n+1} lessons`
      lessons.push(lesson)
    }

    if (isValid) {
      // this.data = data
      this.progressText = `${lessons.length} lessons ready to import`
      this.lessons = {
        items: lessons
      }
    } else {
      this.isValid = false
      this.lessons = undefined
    }

  }

  uploadCover = async () => {
    const { notify, s3UrlCacheStore } = this.props

    const key = `content/${this.values.courseId}/cover.jpg`

    const result = await Storage.put(key, this.values.coverFile, {
      contentType: 'image/jpeg'
    })
      .catch((err: Error) => {
      notify!.show("error", "Unable to upload cover image")
    })

    if (result) {
      this.values.coverUrl = result["key"]
      s3UrlCacheStore!.remove(this.values.coverUrl)
    }
    return result
  }

  downloadCSV = () => {
    if (!this.course) {
      return
    }

    let text = "Lesson,Title,Video URL,Video Duration,Description,Question,Answer A,Answer B,Answer C,Answer D,Correct Answer,Standards\n"
    this.course.lessons.forEach((l: Lesson) => {
      let videoDuration = l.videoDuration ?? 0
      let videoDurationTime = secondsToDuration(videoDuration)
      const q = l.questions[0]
      const correctAnswer = (q.correctAnswer >= 1 && q.correctAnswer <= 4) ? correctAnswers[q.correctAnswer] : ""
      text += `${l.number},"${this.quote(l.title)}","${l.videoUrl}",${videoDurationTime},"${this.quote(l.description)}","${this.quote(q.question)}","${this.quote(q.answers[0])}","${this.quote(q.answers[1])}","${this.quote(q.answers[2])}","${(q.answers[3])}",${correctAnswer},"${this.quote(l.standards)}"\n`
    })

    const blob = new Blob([text], {type: "text/csv;charset=utf-8"});
    saveAs(blob, `${this.values.title}.csv`);
  }

  quote = (text: string): string => {
    return text.replace(/"/g, '""')
  }
}

export default withTheme((withStyles(styles)(CourseEditPage)))